import classNames from "classnames"
import React from "react"

import { extractFromTag, getThemeColor, matchCellPatterns } from "./utils"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import AccordionArrowIcon from "../../../elements/V2/Icons/AccordionArrowIcon"
import Typography from "../../../elements/V2/Typography"

import Logo from "@components/elements/V2/Icons/YocoLogoIcon"
import { Color } from "@constants/V2/color"
import { getAccentColor } from "@utils/V2/color"

type TableHeaderProps = {
  columnHeading: string
  columnIndex: number
  displayIndex: number
  isOpen: boolean
  hasAvailableColumns: boolean
  columnImage?: string
  theme: "light" | "dark"
  backgroundColor: Color
  maxMobileColumns: number
  maxDesktopColumns: number
  onToggleDropdown: () => void
}

const TableHeader = ({
  columnHeading,
  columnIndex,
  displayIndex,
  isOpen,
  hasAvailableColumns,
  columnImage,
  theme,
  backgroundColor,
  maxMobileColumns,
  maxDesktopColumns,
  onToggleDropdown,
}: TableHeaderProps) => {
  const matches = matchCellPatterns(columnHeading)

  return (
    <div
      key={`${columnHeading.replace(" ", "-")}-${columnIndex}`}
      className={classNames({
        "cursor-pointer": hasAvailableColumns,
        "hidden sm:block":
          displayIndex >= maxMobileColumns && displayIndex < maxDesktopColumns,
        hidden: displayIndex >= maxDesktopColumns,
      })}
      onClick={() => {
        if (hasAvailableColumns) {
          onToggleDropdown()
        }
      }}
    >
      <div
        className={classNames(
          "flex items-center gap-20 rounded-10 border-charcoal/10 px-8 py-4 sm:px-16",
          {
            "sm:border": hasAvailableColumns,
            "h-40": !columnImage,
            "h-full min-h-[55px] sm:min-h-[72px]": columnImage,
            "rounded-b-none !border-b-0": isOpen && hasAvailableColumns,
            "shadow-lg": isOpen,
          }
        )}
      >
        {columnImage && (
          <div className="ml-[2px] aspect-square h-32 w-32 sm:h-64 sm:w-64">
            <GatsbyStoryblokImage
              aspectRatio="fullWidth"
              image={extractFromTag(columnImage, "image")}
              alt={columnHeading}
              className="h-32 w-32 sm:h-64 sm:w-64"
              testId="comparison-table-image"
              imageObjectFit="contain"
            />
          </div>
        )}
        <div className="flex flex-col gap-[3px]">
          {matches.isYocoLogo ? (
            <Logo
              className={classNames(
                "max-h-32",
                theme === "light" ? "fill-black" : "fill-white"
              )}
              alt="Yoco Logo"
            />
          ) : (
            <>
              <Typography
                font="grotesk"
                weight="medium"
                color={getThemeColor(theme)}
                size="body-lg"
                text={
                  matches.isProduct
                    ? matches.isProduct[1]
                    : extractFromTag(columnHeading, "title")
                }
                className="text-start"
              />
              {matches.isProduct && matches.isProduct[2] && (
                <Typography
                  font="grotesk"
                  weight="book"
                  color={getThemeColor(theme)}
                  size="body-sm"
                  text={matches.isProduct[2]}
                  className="!hidden text-start sm:!block"
                />
              )}
            </>
          )}
        </div>
        {hasAvailableColumns && (
          <div className="ml-auto pr-8">
            <AccordionArrowIcon
              open={isOpen}
              color={getAccentColor(
                theme === "dark"
                  ? backgroundColor === Color.Black
                    ? Color.Black
                    : Color.Charcoal
                  : Color.White
              )}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default TableHeader
